import { useCallback, useMemo, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useFlags } from 'launchdarkly-react-client-sdk';
import debounce from 'lodash/debounce';
import { useAppThunkDispatch } from 'state/store';
import {
  fetchAvailableAssignees,
  fetchMoreAvailableAssignees,
  selectAvailableAssigneesWithCheckedStatus,
  FetchAvailableAssigneesParams,
  setIsAssigneesListVisible,
  selectIsAssigneesListVisible,
  selectIsFetchingAvailableAssignees,
  selectIsFetchingMoreAvailableAssignees,
  selectCanFetchMoreAvailableAssignees,
} from 'state/ProjectTasksList/bulkActions/bulkAssign/availableAssignees/availableAssigneesSlice';
import {
  bulkUpdateTasksAssignees,
  selectIsBulkAssignLoading,
} from 'state/ProjectTasksList/bulkActions/bulkAssign/bulkAssignSlice';
import { displayInProgressNotification } from 'state/ProjectTasksList/bulkActions/bulkActionsSlice';
import { setShouldRefreshTasks } from 'state/ProjectTasksList/actions/actionsSlice';
import { selectEditableTasksCount } from 'state/ProjectTasksList/tasksMetrics/taskMetricsSlice';
import {
  selectSelectedTasksIds,
  selectAreAllTasksSelected,
  selectExcludedTasksIds,
} from 'state/ProjectTasksList/export/exportSlice';
import { TaskDetailToUpdate } from 'types/store/taskDetail';

export const useBulkAssign = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const { bulkTaskAssignUndo = false } = useFlags();
  const availableAssignees = useSelector(
    selectAvailableAssigneesWithCheckedStatus
  );
  const isAssigneesListVisible = useSelector(selectIsAssigneesListVisible);
  const isFetchingAvailableAssignees = useSelector(
    selectIsFetchingAvailableAssignees
  );
  const isFetchingMoreAvailableAssignees = useSelector(
    selectIsFetchingMoreAvailableAssignees
  );
  const editableTasksCount = useSelector(selectEditableTasksCount);
  const isBulkAssignLoading = useSelector(selectIsBulkAssignLoading);
  const canFetchMoreAvailableAssignees = useSelector(
    selectCanFetchMoreAvailableAssignees
  );
  const selectedTasksIds = useSelector(selectSelectedTasksIds);
  const areAllTasksSelected = useSelector(selectAreAllTasksSelected);
  const excludedTasksIds = useSelector(selectExcludedTasksIds);
  const dispatch = useAppThunkDispatch();

  // Track the last search term to refetch with the same criteria after bulk update
  const lastSearchTermRef = useRef<string>('');

  // This is used to detect when selection changes
  const selectionKey = useMemo(() => {
    return `${areAllTasksSelected}-${selectedTasksIds.join(
      ','
    )}-${excludedTasksIds.join(',')}`;
  }, [areAllTasksSelected, selectedTasksIds, excludedTasksIds]);

  const debouncedDisplayInProgressNotification = useMemo(() => {
    return debounce(() => {
      dispatch(displayInProgressNotification());
    }, 3000);
  }, [dispatch]);

  const fetchAvailableTaskAssignees = useCallback(
    async (params: FetchAvailableAssigneesParams) => {
      await dispatch(
        fetchAvailableAssignees({
          ...params,
          projectId,
          includeAssignmentStats: true,
        })
      );
    },
    [dispatch, projectId]
  );

  const handleSearchChange = useCallback(
    async (value: string) => {
      lastSearchTermRef.current = value;
      await fetchAvailableTaskAssignees({
        name: value,
      });
    },
    [fetchAvailableTaskAssignees]
  );

  const handleUpdateSelectedTaskAssignees = useCallback(
    async (selectedUsers: string[], unselectedUsers: string[]) => {
      const task: Partial<TaskDetailToUpdate> = {
        assignees: { toAdd: selectedUsers, toRemove: unselectedUsers },
      };
      debouncedDisplayInProgressNotification();
      const result = await dispatch(
        bulkUpdateTasksAssignees({
          task,
          projectId,
          config: {
            headers: {
              'notification-type': 'in_app',
            },
          },
          canUndo: bulkTaskAssignUndo,
        })
      );

      // Refetch available assignees after successful bulk update to update checked status
      if (
        bulkUpdateTasksAssignees.fulfilled.match(result) &&
        isAssigneesListVisible
      ) {
        await fetchAvailableTaskAssignees({
          name: lastSearchTermRef.current || undefined,
        });
      }

      dispatch(setShouldRefreshTasks(true));
    },
    [
      dispatch,
      projectId,
      fetchAvailableTaskAssignees,
      isAssigneesListVisible,
      debouncedDisplayInProgressNotification,
      bulkTaskAssignUndo,
    ]
  );

  const handleExpandedChange = useCallback(
    async (isExpanded: boolean) => {
      dispatch(setIsAssigneesListVisible(isExpanded));
      if (isExpanded) {
        lastSearchTermRef.current = '';
        await fetchAvailableTaskAssignees({});
      }
    },
    [dispatch, fetchAvailableTaskAssignees]
  );

  const loadMoreAvailableAssignees = useCallback(async () => {
    await dispatch(fetchMoreAvailableAssignees());
  }, [dispatch]);

  const prevSelectionKeyRef = useRef<string>(selectionKey);

  // Refetch assignees when selection changes while dropdown is visible
  useEffect(() => {
    const selectionChanged = prevSelectionKeyRef.current !== selectionKey;
    prevSelectionKeyRef.current = selectionKey;

    if (isAssigneesListVisible && selectionChanged) {
      fetchAvailableTaskAssignees({
        name: lastSearchTermRef.current || undefined,
      });
    }
  }, [selectionKey, isAssigneesListVisible, fetchAvailableTaskAssignees]);

  const isDisabled = useMemo(() => {
    return editableTasksCount !== undefined ? editableTasksCount === 0 : false;
  }, [editableTasksCount]);

  return useMemo(() => {
    return {
      availableAssignees,
      handleSearchChange,
      handleUpdateSelectedTaskAssignees,
      fetchAvailableTaskAssignees,
      isFetchingAvailableAssignees,
      isFetchingMoreAvailableAssignees,
      handleExpandedChange,
      isDisabled,
      isBulkAssignLoading,
      canFetchMoreAvailableAssignees,
      loadMoreAvailableAssignees,
    };
  }, [
    availableAssignees,
    handleSearchChange,
    handleUpdateSelectedTaskAssignees,
    fetchAvailableTaskAssignees,
    isFetchingAvailableAssignees,
    isFetchingMoreAvailableAssignees,
    handleExpandedChange,
    isDisabled,
    isBulkAssignLoading,
    canFetchMoreAvailableAssignees,
    loadMoreAvailableAssignees,
  ]);
};
